import React, { useState, useEffect, useRef } from "react";
import { AuthLogo } from "@/components/Auth/AuthLogo";
import { PasswordInput } from "@/components/Auth/PasswordInput";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";
import { Loader2, CheckCircle2 } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { TurnstileWidget, type TurnstileWidgetHandle } from "@/components/Auth/TurnstileWidget";

const resetPasswordSchema = z.object({
  password: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

export default function ResetPasswordPage() {
  usePageMeta("Reset Password — Shattara AI", "", { noIndex: true });
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidSession, setIsValidSession] = useState<boolean | null>(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user has a valid recovery session
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      // Check if user came from a password reset email
      // The session will exist if they clicked the reset link
      if (session) {
        setIsValidSession(true);
      } else {
        // Listen for auth state changes (recovery token processing)
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            if (event === "PASSWORD_RECOVERY" && session) {
              setIsValidSession(true);
            } else if (event === "SIGNED_IN" && session) {
              // User might already be signed in from recovery link
              setIsValidSession(true);
            }
          }
        );

        // Set a timeout for invalid session detection
        setTimeout(() => {
          if (isValidSession === null) {
            setIsValidSession(false);
          }
        }, 2000);

        return () => subscription.unsubscribe();
      }
    };

    checkSession();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError("");
    setConfirmPasswordError("");

    // Validate form
    const result = resetPasswordSchema.safeParse({ password, confirmPassword });
    if (!result.success) {
      const errors = result.error.flatten().fieldErrors;
      if (errors.password) setPasswordError(errors.password[0]);
      if (errors.confirmPassword) setConfirmPasswordError(errors.confirmPassword[0]);
      return;
    }

    setIsLoading(true);

    const resetTurnstile = () => {
      setTurnstileToken("");
      turnstileRef.current?.reset();
    };

    // Verify Turnstile token server-side
    const { data: verifyData, error: verifyError } = await supabase.functions.invoke("verify-turnstile", {
      body: { token: turnstileToken },
    });
    if (verifyError || !verifyData?.success) {
      toast.error("Bot verification failed. Please try again.");
      resetTurnstile();
      setIsLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        toast.error(error.message);
        resetTurnstile();
        return;
      }

      setIsSuccess(true);
      toast.success("Password updated successfully!");
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    } catch (error: any) {
      toast.error(error.message || "An error occurred");
      resetTurnstile();
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while checking session
  if (isValidSession === null) {
    return (
      <main className="flex-grow px-6 flex flex-col items-center justify-center min-h-screen bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </main>
    );
  }

  // Show error if no valid recovery session
  if (isValidSession === false) {
    return (
      <main className="flex-grow px-6 flex flex-col items-center justify-center min-h-screen bg-background animate-in fade-in duration-500">
        <div className="w-full max-w-md space-y-6 animate-in slide-in-from-bottom-4 duration-700">
          <div className="flex justify-center">
            <AuthLogo />
          </div>
          
          <section className="flex flex-col items-center justify-center gap-4 text-center">
            <h1 className="text-2xl font-semibold">Invalid or Expired Link</h1>
            <p className="text-muted-foreground">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            
            <Link to="/auth/forgotpassword">
              <Button className="mt-4">Request New Link</Button>
            </Link>
            
            <div className="mt-4 text-muted-foreground text-sm">
              Remembered your password?{" "}
              <Link
                to="/auth/signin"
                className="text-foreground underline hover:text-primary transition-colors duration-200"
              >
                Sign in
              </Link>
            </div>
          </section>
        </div>
      </main>
    );
  }

  // Show success state
  if (isSuccess) {
    return (
      <main className="flex-grow px-6 flex flex-col items-center justify-center min-h-screen bg-background animate-in fade-in duration-500">
        <div className="w-full max-w-md space-y-6 animate-in slide-in-from-bottom-4 duration-700">
          <div className="flex justify-center">
            <AuthLogo />
          </div>
          
          <section className="flex flex-col items-center justify-center gap-4 text-center">
            <CheckCircle2 className="h-16 w-16 text-green-500" />
            <h1 className="text-2xl font-semibold">Password Updated!</h1>
            <p className="text-muted-foreground">
              Your password has been successfully updated. Redirecting to dashboard...
            </p>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-grow px-6 flex flex-col items-center justify-center min-h-screen bg-background animate-in fade-in duration-500">
      <div className="w-full max-w-md space-y-6 animate-in slide-in-from-bottom-4 duration-700">
        <div className="flex justify-center">
          <AuthLogo />
        </div>
        
        <section className="flex flex-col items-center justify-center gap-2">
          <h1 className="text-2xl font-semibold">Set New Password</h1>
          <p className="text-muted-foreground text-center">
            Enter your new password below.
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-4 w-full max-w-md">
            <PasswordInput
              password={password}
              setPassword={setPassword}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              error={passwordError}
              placeholder="New password"
            />
            
            <PasswordInput
              password={confirmPassword}
              setPassword={setConfirmPassword}
              showPassword={showConfirmPassword}
              setShowPassword={setShowConfirmPassword}
              error={confirmPasswordError}
              placeholder="Confirm new password"
            />

            <Button
              type="submit"
              disabled={isLoading || !turnstileToken}
              className="w-full h-12 mt-2 transition-all duration-200 hover:scale-[1.02]"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating password...
                </>
              ) : (
                "Update Password"
              )}
            </Button>
          </form>

          <div className="mt-4 text-muted-foreground text-sm">
            Remembered your password?{" "}
            <Link
              to="/auth/signin"
              className="text-foreground underline hover:text-primary transition-colors duration-200"
            >
              Sign in
            </Link>
          </div>

          <TurnstileWidget
            ref={turnstileRef}
            onVerify={setTurnstileToken}
            onExpire={() => setTurnstileToken("")}
            onError={() => setTurnstileToken("")}
          />
        </section>
      </div>
    </main>
  );
}
